import { useEffect, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Type, Code } from '@/shared/schema';
import { useFormContext } from 'react-hook-form';
import RuleGroup from './RuleGroup';
import RuleCondition from './RuleCondition';
import {
    RuleStructure,
    Condition,
    ConditionGroup,
    LogicalOperator,
    OperatorType,
    generateId,
    findGroupById,
    findConditionById,
    buildRuleStringFromStructure,
    buildFormattedRuleStringFromStructure,
    extractDatatypesAndAttributes
} from './nestedRuleFunctions';
import DropdownSelect from '@/components/uiCustomComps/DropdownSelect';

interface RuleGenerateProps {
    dataType: Type[];
    attribute: Code[];
    setAttrCode: (code: string) => void;
    version: boolean
}

const RuleGenerate = ({ dataType, attribute, setAttrCode, version }: RuleGenerateProps) => {
    const { watch, setValue } = useFormContext();
    const ruleType = watch("ruleType");

    const ruleStructure: RuleStructure = watch('NestedRule') || {
        groups: [
            {
                id: 'group-1',
                type: 'group',
                conditions: [
                    {
                        id: 'cond-1',
                        dataType: '',
                        attribute: '',
                        operator: '==',
                        valueType: 'operand',
                        dataType2: '',
                        attribute2: ''
                    }
                ],
                logicalOperators: []
            }
        ],
        groupOperators: []
    };
    // console.log("ruleStructure", ruleStructure)
    const operators: OperatorType[] = ['==', '!=', '>', '<', '>=', '<='];

    const updateRuleStructure = (updatedRule: RuleStructure) => {
        setValue('NestedRule', updatedRule);
    };

    const ruleStructureJSON = useMemo(() =>
        JSON.stringify(ruleStructure),
        [ruleStructure]
    );

    useEffect(() => {
        const ruleString = buildRuleStringFromStructure(
            ruleStructure.groups,
            ruleStructure.groupOperators
        );
        setValue('NestedRuleString', ruleString);

        const rawRuleString = buildFormattedRuleStringFromStructure(
            ruleStructure.groups,
            ruleStructure.groupOperators
        )
        setValue('rawNestedRuleString', rawRuleString);

        const datatypeAttributeMap = extractDatatypesAndAttributes(ruleStructure.groups);
        setValue('DatatypeAttributeMap', datatypeAttributeMap);
    }, [ruleStructureJSON]);

    const addCondition = (groupId: string) => {
        const newCondition: Condition = {
            id: generateId('cond'),
            dataType: '',
            attribute: '',
            operator: '==',
            valueType: 'operand',
            dataType2: '',
            attribute2: ''
        };

        const updated = JSON.parse(JSON.stringify(ruleStructure));
        const group = findGroupById(updated.groups, groupId);
        if (group) {
            group.conditions.push(newCondition);
            group.logicalOperators.push('AND');
        }
        updateRuleStructure(updated);
    };

    const addSubgroup = (parentGroupId: string) => {
        const newSubgroup: ConditionGroup = {
            id: generateId('subgroup'),
            type: 'subgroup',
            conditions: [
                {
                    id: generateId('cond'),
                    dataType: '',
                    attribute: '',
                    operator: '==',
                    valueType: 'operand',
                    dataType2: '',
                    attribute2: ''
                }
            ],
            logicalOperators: []
        };

        const updated = JSON.parse(JSON.stringify(ruleStructure));
        const group = findGroupById(updated.groups, parentGroupId);
        if (group) {
            group.conditions.push(newSubgroup);
            group.logicalOperators.push('AND');
        }
        updateRuleStructure(updated);
    }

    const deleteGroup = (groupId: string) => {
        const updated = JSON.parse(JSON.stringify(ruleStructure));

        const deleteFromGroups = (groups: any[], parentGroup?: any): boolean => {
            for (let i = 0; i < groups.length; i++) {
                const item = groups[i];

                if (item.id === groupId) {
                    if (!parentGroup) {
                        groups.splice(i, 1);
                        if (i < updated.groupOperators.length) {
                            updated.groupOperators.splice(i, 1);
                        } else if (updated.groupOperators.length > 0) {
                            updated.groupOperators.splice(updated.groupOperators.length - 1, 1);
                        }
                    } else {
                        parentGroup.conditions.splice(i, 1);
                        if (i < parentGroup.logicalOperators.length) {
                            parentGroup.logicalOperators.splice(i, 1);
                        } else if (parentGroup.logicalOperators.length > 0) {
                            parentGroup.logicalOperators.splice(parentGroup.logicalOperators.length - 1, 1);
                        }
                    }
                    return true;
                }

                if ('conditions' in item) {
                    if (deleteFromGroups(item.conditions, item)) {
                        return true;
                    }
                }
            }
            return false;
        };

        deleteFromGroups(updated.groups);
        updateRuleStructure(updated);
    };

    const addGroup = () => {
        const newGroup: ConditionGroup = {
            id: generateId('group'),
            type: 'group',
            conditions: [
                {
                    id: generateId('cond'),
                    dataType: '',
                    attribute: '',
                    operator: '==',
                    valueType: 'operand',
                    dataType2: '',
                    attribute2: ''
                }
            ],
            logicalOperators: []
        };

        updateRuleStructure({
            ...ruleStructure,
            groups: [...ruleStructure.groups, newGroup],
            groupOperators: [...ruleStructure.groupOperators, 'AND']
        });
    };

    const addTopLevelCondition = () => {
        const newCondition: Condition = {
            id: generateId('cond'),
            dataType: '',
            attribute: '',
            operator: '==',
            valueType: 'operand',
            dataType2: '',
            attribute2: ''
        };

        updateRuleStructure({
            ...ruleStructure,
            groups: [...ruleStructure.groups, newCondition],
            groupOperators: [...ruleStructure.groupOperators, 'AND']
        });
    };

    const updateCondition = (condId: string, field: string, value: any) => {
        const updated = JSON.parse(JSON.stringify(ruleStructure));
        const condition = findConditionById(updated.groups, condId);
        if (condition) {
            condition[field] = value;
            if (field === 'valueType') {
                if (value === 'value') {
                    condition.dataType2 = '';
                    condition.attribute2 = '';
                    condition.value = '';
                } else {
                    condition.value = '';
                }
            }
        }
        updateRuleStructure(updated);
    };

    const updateDatatype = (condId: string, field: string, value: any) => {
        setAttrCode(value?.ID);
        updateCondition(condId, field, value?.Name);
    };

    const updateLogicalOperator = (groupId: string, index: number, operator: LogicalOperator) => {
        const updated = JSON.parse(JSON.stringify(ruleStructure));
        const group = findGroupById(updated.groups, groupId);
        if (group && group.logicalOperators) {
            group.logicalOperators[index] = operator;
        }
        updateRuleStructure(updated);
    };

    const updateGroupOperator = (index: number, operator: LogicalOperator) => {
        updateRuleStructure({
            ...ruleStructure,
            groupOperators: ruleStructure.groupOperators.map((op, i) => i === index ? operator : op)
        });
    };

    const deleteCondition = (groupId: string, condId: string) => {
        const updated = JSON.parse(JSON.stringify(ruleStructure));
        const group = findGroupById(updated.groups, groupId);
        if (group) {
            const index = group.conditions.findIndex((c: any) => c.id === condId);
            if (index > -1) {
                group.conditions.splice(index, 1);
                if (index < group.logicalOperators.length) {
                    group.logicalOperators.splice(index, 1);
                } else if (group.logicalOperators.length > 0) {
                    group.logicalOperators.splice(group.logicalOperators.length - 1, 1);
                }
            }
        } else {
            const index = updated.groups.findIndex((c: any) => c.id === condId);
            if (index > -1) {
                updated.groups.splice(index, 1);
                if (index < updated.groupOperators.length) {
                    updated.groupOperators.splice(index, 1);
                } else if (updated.groupOperators.length > 0) {
                    updated.groupOperators.splice(updated.groupOperators.length - 1, 1);
                }
            }
        }
        updateRuleStructure(updated);
    };

    return (
        <div className="mx-auto p-2 space-y-6">
            <div className='flex justify-between'>
                <div>
                    <h2 className="text-lg font-semibold">{ruleType.label}</h2>
                </div>
                <div className="flex gap-3 justify-end">
                    <Button
                        onClick={addGroup}
                        size="sm"
                        className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
                    >
                        <Plus className="w-5 h-5" />
                        Add Group
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={addTopLevelCondition}
                        className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                    >
                        <Plus className="w-5 h-5" />
                        Add Condition
                    </Button>
                </div>
            </div>

            <div className="space-y-2">
                {ruleStructure?.groups?.map((item, index) => {
                    if ('conditions' in item) {
                        return (
                            <RuleGroup
                                key={item.id}
                                group={item}
                                level={0}
                                groupIndex={index}
                                rule={ruleStructure}
                                dataType={dataType}
                                attribute={attribute}
                                operators={operators}
                                onAddCondition={addCondition}
                                onAddSubgroup={addSubgroup}
                                onUpdateCondition={updateCondition}
                                onUpdateDatatype={updateDatatype}
                                onUpdateLogicalOperator={updateLogicalOperator}
                                onUpdateGroupOperator={updateGroupOperator}
                                onDeleteCondition={deleteCondition}
                                onDeleteGroup={deleteGroup}
                                isFirstGroup={false}
                                version={version}
                            />
                        );
                    } else {
                        return (
                            <div key={item.id} className="space-y-2">
                                {index > 0 && (
                                    <div className="flex items-center gap-2">
                                        <DropdownSelect
                                            value={ruleStructure.groupOperators[index - 1] || 'AND'}
                                            onValueChange={(value) => {
                                                updateGroupOperator(index - 1, value as LogicalOperator)
                                            }}
                                            options={[{ value: "AND", label: "AND" }, { value: "OR", label: "OR" }]}
                                        />
                                    </div>
                                )}
                                <RuleCondition
                                    condition={item}
                                    groupId="root"
                                    index={index}
                                    groupOperators={ruleStructure.groupOperators}
                                    isFirstInGroup={index === 0}
                                    dataType={dataType}
                                    operators={operators}
                                    onUpdateCondition={updateCondition}
                                    onUpdateDatatype={updateDatatype}
                                    onUpdateLogicalOperator={updateLogicalOperator}
                                    onDeleteCondition={deleteCondition}
                                    version={version}
                                />
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    );
};

export default RuleGenerate;